import { supabase, requireSession } from "@/lib/supabase/client";
import type { Json } from "@/lib/supabase/database.types";
import type { UserProfile } from "@/types";
import { migrateProfile, PROFILE_SCHEMA_VERSION } from "@/lib/data/schemaVersions";
import { nextClientRevision, reliableWrite, rememberClientRevision } from "@/lib/persistence/reliableWrite";

/**
 * Profile Service — reads and writes UserProfile to Supabase.
 *
 * The entire UserProfile is stored as JSONB in profiles.data.
 * This means we never need a schema migration when the profile shape changes.
 */
export const profileService = {
  /**
   * Load the profile for the current user.
   * Returns null if the row doesn't exist yet (first login before onboarding).
   */
  async load(userId: string): Promise<UserProfile | null> {
    const { data, error } = await supabase
      .from("profiles")
      .select("data")
      .eq("id", userId)
      .single();

    if (error) {
      // PGRST116 = "no rows" — user hasn't completed onboarding yet, not an error.
      if (error.code !== "PGRST116") {
        console.error("[profileService.load] failed:", error.message);
      }
      return null;
    }

    if (!data?.data) return null;

    const profile = migrateProfile(data.data);
    if (profile) rememberClientRevision(`profile:${userId}`, profile.revision ?? 0);
    return profile;
  },

  /**
   * Save (upsert) the full profile for the current user.
   * Call this whenever the profile changes in profileStore.
   */
  async save(userId: string, profile: UserProfile): Promise<void> {
    await requireSession(userId);
    return reliableWrite(`profile:${userId}`, async () => {
    const key = `profile:${userId}`;
    const versioned = { ...profile, schemaVersion: PROFILE_SCHEMA_VERSION, revision: nextClientRevision(key, profile.revision) };
    const { data: accepted, error: rpcError } = await supabase.rpc("save_profile_if_newer", {
      p_user_id: userId,
      p_data: versioned as unknown as Json,
    });
    if (!rpcError && accepted === true) return;
    if (!rpcError && accepted === false) throw new Error("A newer profile was saved on another device. Reload before trying again.");
    // Older deployments may not have the conflict RPC yet. The RLS-protected
    // upsert keeps saves working during a rolling migration.
    const { error } = await supabase.from("profiles").upsert(
      {
        id: userId,
        data: versioned as unknown as Json,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "id" },
    );

    if (error)
      throw new Error(
        `[profileService.save] ${error.message} (code: ${error.code}; rpc: ${rpcError?.code ?? "empty-response"})${
          error.hint ? ` — ${error.hint}` : ""
        }`,
      );
    });
  },
};
